import React from 'react'
import {BRAND_LABEL} from '../brand'
import {findImageGroupBy} from './image-groups-utils'
import {MRT_API_BASEPATH} from '../ssr/constants'

/**
 * Clears unwanted spaces of a string value separated by token and returns an array
 *
 * @param values - string (ex. 'category , question,answer')
 * @param token - string (ex. ',')
 * @return {array} - => ['category', 'question', 'answer']
 */
export const sanitizeValues = (values, token = ',') => values.replace(/\s/g, '').split(token)
export const remToPxConverter = (remValue) => parseFloat(remValue) * 16
export const sortAlphabetically = (a, b) => {
    if (a.label < b.label) return -1
    if (a.label > b.label) return 1
    return 0
}
/**
 * Formats a given date (or the current date) as "YYYY-MM-DD".
 *
 * @param {Date} [date=new Date()] - The date to format. Defaults to the current date.
 * @returns {string} The formatted date in "YYYY-MM-DD" format.
 */
export const formatDateToYYYYMMDD = (date = new Date()) => {
    return date.toISOString().split('T')[0] // Extracts only the date part
}
/**
 * Formats a given date string as "YYYYMMDD000000".
 *
 * @param {string} dateStr - The date string to format in "DD/MM/YYYY" format.
 * @returns {string} The formatted date in "YYYYMMDD000000" format.
 */
export const convertDateToYYYYMMDD000000 = (dateStr = '') => {
    if (!dateStr) {
        console.error('Invalid date string:', dateStr)
        return ''
    }

    const date = new Date(dateStr)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}000000`
}

/**
 * Converts a date string in "YYYYMMDD000000" format to a JavaScript Date object.
 *
 * @param {string} dateStr - The date string in "YYYYMMDD000000" format.
 * @returns {Date|null} A valid Date object or null if the input is invalid.
 */
export const convertYYYYMMDD000000ToDate = (dateStr) => {
    if (!dateStr || dateStr.length < 8) {
        console.error('Invalid date string:', dateStr)
        return null
    }
    const year = parseInt(dateStr.slice(0, 4), 10)
    const month = parseInt(dateStr.slice(4, 6), 10) - 1 // Months are 0-based in JavaScript
    const day = parseInt(dateStr.slice(6, 8), 10)
    // Set to 23:00:00 UTC to preserve date in US timezones, since Marketing Cloud has US timezone;
    // sending date at just midnight may result in previous date in US timezones
    const date = new Date(Date.UTC(year, month, day, 23, 0, 0))
    return date
}

/**
 * Returns a formatted date string in the format "YYYY/MM/DD HH:MM"
 * for use as an entry timestamp (e.g., logs, user activity, etc.).
 *
 * Example output: "2025/04/16 09:06"
 *
 * @param {Date} - Optional JavaScript Date object. Defaults to current date and time.
 * @returns {string} A formatted date string in "YYYY/MM/DD HH:MM" format.
 */
export const getFormattedEntryDateString = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}/${month}/${day} ${hours}:${minutes}`
}

export const setWithExpiry = (key, value, ttl) => {
    if (typeof window === 'undefined') return null
    const now = new Date()
    const item = {
        value,
        expiry: now.getTime() + ttl,
    }
    localStorage.setItem(key, JSON.stringify(item))
}

export const getWithExpiry = (key) => {
    if (typeof window === 'undefined') return null
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null

    const item = JSON.parse(itemStr)
    const now = new Date()

    if (now.getTime() > item.expiry) {
        localStorage.removeItem(key)
        return null
    }

    return true
}

/**
 * Generates a random GUID (UUID v4 format).
 *
 * @returns {string} A randomly generated GUID in the format "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".
 *
 * - Uses `Math.random()` to generate random values.
 * - Ensures the correct UUID v4 format by setting specific bits.
 * - Converts values to hexadecimal representation.
 */
export const generateGUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = (Math.random() * 16) | 0 // Generates a random number between 0-15
        let v = c === 'x' ? r : (r & 0x3) | 0x8 // Ensures correct UUID v4 format
        return v.toString(16) // Converts to hex
    })
}

/**
 * Fetch a new access token from the API.
 * @param {string} apiUrl - The API endpoint to fetch the token.
 * @param {string} accessTokenKey - The key for storing the access token in local storage.
 * @param {string} tokenExpirationKey - The key for storing the token expiration time in local storage.
 * @returns {Promise<string>} - The new access token.
 */
export const fetchAccessToken = async (
    apiUrl,
    accessTokenKey,
    tokenExpirationKey,
    locale,
    customerId,
    token,
) => {
    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                customerid: customerId,
                authorization: token,
            },
            body: JSON.stringify({locale}),
        })

        if (!response.ok) {
            throw new Error('Failed to fetch access token')
        }

        const data = await response.json()

        if (data.accessToken) {
            return data.accessToken
        } else {
            throw new Error('Invalid token response from API')
        }
    } catch (error) {
        console.error('Error fetching access token:', error)
        throw error
    }
}

/**
 * Get a valid token from local storage or fetch a new one if missing/expired.
 * @param {string} apiUrl - The API endpoint to fetch the token.
 * @param {string} accessTokenKey - The key for storing the access token in local storage.
 * @param {string} tokenExpirationKey - The key for storing the token expiration time in local storage.
 * @returns {Promise<string>} - A valid access token.
 */
export const getValidToken = async (
    apiUrl,
    accessTokenKey,
    tokenExpirationKey,
    locale,
    customerId,
    token,
) => {
    // Fetch a new token if no valid token exists
    return await fetchAccessToken(
        apiUrl,
        accessTokenKey,
        tokenExpirationKey,
        locale,
        customerId,
        token,
    )
}

export const PRODUCT_REGISTRATION_DUPLICATION_KEY = 'productRegistrationDuplication'

export const getSavedSerialNumberDuplicationToken = () => {
    if (typeof window === 'undefined') return null
    try {
        const saved = JSON.parse(sessionStorage.getItem(PRODUCT_REGISTRATION_DUPLICATION_KEY))
        return typeof saved?.duplicationToken === 'string' ? saved.duplicationToken : null
    } catch {
        return null
    }
}

export const fetchSerialNumberDuplication = async (appOrigin, accessToken, serialNumber) => {
    if (!serialNumber) return {hasDuplicates: false, duplicationToken: null}
    try {
        const response = await fetch(
            `${appOrigin}${MRT_API_BASEPATH}/check-serial-number-duplication-sap-cpi`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({SerialNumber: serialNumber}),
            },
        )
        const result = await response.json()
        return result?.success ? result : {hasDuplicates: false, duplicationToken: null}
    } catch (error) {
        console.error('Error during serial number duplication check:', error)
        return {hasDuplicates: false, duplicationToken: null}
    }
}

export const fetchSerialNumberRegistrationDecision = async (
    appOrigin,
    accessToken,
    duplicationToken,
    userId,
    purchaseDate,
    identityOnly = false,
) => {
    if (!duplicationToken) return {blocked: false}
    const response = await fetch(
        `${appOrigin}${MRT_API_BASEPATH}/check-serial-number-duplication-sap-cpi`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({duplicationToken, userId, purchaseDate, identityOnly}),
        },
    )
    const result = await response.json()
    if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Serial number duplication decision failed')
    }
    return {blocked: result.blocked}
}

/**
 * Clears the stored access token.
 * @param {string} accessTokenKey - The key for the access token in local storage.
 * @param {string} tokenExpirationKey - The key for the expiration time in local storage.
 */
export const clearAuthToken = (accessTokenKey, tokenExpirationKey) => {
    localStorage.removeItem(accessTokenKey)
    localStorage.removeItem(tokenExpirationKey)
}

export const hashSHA256 = async (str) => {
    if (!str) return
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArr = Array.from(new Uint8Array(hashBuffer))
    return hashArr.map((x) => x.toString(16).padStart(2, '0')).join('')
}

/**
 * - This function is used to apply the hmacSHA256 encryption on a key - data pair
 * @param {String} key - The key used in the encription process
 * @param {String} data - The stringified JSON object
 * @returns {String} - A string that has applied the hmacSHA256 encription using the key and the data
 */
export const hmacSHA256 = async (key, data) => {
    if (!key || !data) return

    const encodedKey = new TextEncoder().encode(key)
    const encodedData = new TextEncoder().encode(data)

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        encodedKey,
        {name: 'HMAC', hash: {name: 'SHA-256'}},
        false,
        ['sign'],
    )

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, encodedData)
    return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 *
 * @param {*} code
 * @returns The language on its respective locale
 * @example
 * getLanguage('en') // English
 * getLanguage('it') // Italiano
 * getLanguage('fr') // Français
 */

export const getLanguage = (code) => {
    const lang = new Intl.DisplayNames([code], {type: 'language'})
    return lang.of(code)
}

/**
 *
 * @param {*} code
 * @returns The region on its respective locale
 * @example
 */

export const getRegion = (language, country) => {
    const lang = new Intl.DisplayNames([language], {type: 'region'})
    return lang.of(country)
}

// Function to calculate the initial map of material groups
export const calculateCountMap = (products) => {
    return Object.keys(products).reduce((acc, materialGroup) => {
        acc[materialGroup] = 0
        return acc
    }, {})
}

/**
 * Sums Account.Counter fields for each material group ONCE using only the first record's Account.
 * @param {Object} materialGroupCount - The map of material group codes to current counts (will be mutated).
 * @param {Array} orderSummaries - Array of order summary objects (from OMS API).
 */
export const sumAccountCountersForMaterialGroups = (materialGroupCount, orderSummaries) => {
    if (!orderSummaries || orderSummaries.length === 0) return
    const account = orderSummaries[0].Account || {}
    Object.keys(materialGroupCount).forEach((materialGroup) => {
        const counterField = `Counter${materialGroup}__c`
        const counterValue = account[counterField]
        if (
            Object.prototype.hasOwnProperty.call(account, counterField) &&
            !isNaN(parseInt(counterValue))
        ) {
            materialGroupCount[materialGroup] += parseInt(counterValue) || 0
        }
    })
}

// Function to update the map with the order records
export const updateCountMap = (countMap, records) => {
    // Sum OrderItemSummaries quantities
    records
        .flatMap((order) => order.OrderItemSummaries?.records || [])
        .forEach((item) => {
            const rawCode = item.ProductMaterialGroup__c
            if (!rawCode) return
            // ASSUMPTION: second-hand products have a material group code starting with '2' and then same as the original product
            // Normalize material group: treat codes starting with '2' (e.g., '2HHCMFA')
            // as contributions to their base group ('HHCMFA').
            const normalizedCode = rawCode.startsWith('2') ? rawCode.slice(1) : rawCode
            if (countMap[normalizedCode] !== undefined) {
                countMap[normalizedCode] += item.Quantity || 0
            }
        })
}

// Function to get the material groups with the display value
export const getMaterialGroups = (products, countMap) => {
    return Object.entries(products).map(([materialGroup, maxValue]) => ({
        materialGroup,
        displayValue: `${countMap[materialGroup]} / ${maxValue}`,
    }))
}

/**
 * Generates meta tags based on the provided meta tag rules.
 * For each object in the array `metaTags`, the function creates a `<meta>` tag.
 * - If the `ID` of the meta tag is "description", it uses `name` as the attribute.
 * - For all other IDs, it uses `property` as the attribute.
 * @param {Array} metaTags - An array of objects where each object represents a meta tag rule.
 * @param {string} defaultTitle - The default title to use if no title meta tag is found.
 * @returns {JSX.Element[]} An array of JSX elements representing the meta tags, ready to be rendered.
 */
export const getMetaTags = (metaTags = [], defaultTitle = BRAND_LABEL) => {
    const hasTitleTag = metaTags?.some(
        (metaTag) => metaTag.ID === 'title' || metaTag.id === 'title',
    )

    const metaElements = metaTags?.map((metaTag, index) => {
        const isDescriptionTag = metaTag.ID === 'description' || metaTag.id === 'description'
        const isTitleTag = metaTag.ID === 'title' || metaTag.id === 'title'
        const key = `meta-tag-${index}`
        if (isTitleTag) {
            return <title key={key}>{metaTag.content || metaTag.value}</title>
        }
        return (
            <meta
                key={key}
                {...(isDescriptionTag
                    ? {name: metaTag.ID || metaTag.id}
                    : {property: metaTag.ID || metaTag.id})}
                content={metaTag.content || metaTag.value || ''}
            />
        )
    })
    if (!hasTitleTag) {
        metaElements.unshift(<title key="default-title">{defaultTitle}</title>)
    }

    return metaElements
}

/**
 * Parses a JSON string after removing all whitespace characters and correcting trailing commas.
 *
 * @param {string} string - The JSON string to parse.
 * @returns {Object|null} The parsed JSON object, or null if the input string is falsy.
 */
export const parseAndSanitizeJSON = (string) => {
    if (!string) return null

    try {
        string = string.replaceAll(/\s/g, '').replaceAll(',}', '}').replaceAll('}{', '},{')
        return JSON.parse(string)
    } catch (e) {
        console.error(string, e)
        return ''
    }
}

/**
 * Parses a JSON string after removing newlines, tabs, spaces around JSON symbols,fixes trailing commas,ensures objects are comma-separated
 * @param {string} string - THe JSON string to parse.
 * @returns {Object|null} The parsed JSON object, or null if the input string is falsy.
 */
export const parseAndSanitizeJSONWithSpaces = (string, id) => {
    if (!string) return null

    try {
        string = string
            ?.replace(/\r/g, '')
            .replace(/\n/g, '')
            .replace(/\t/g, '')
            .replace(/\s*([{}[\]])\s*/g, '$1')
            .replace(/\*\s+(?=\S)/g, '*')
            .replace(/,}/g, '}')
            .replace(/}{/g, '},{')
            .replace(/(?<!\d)([,:;.!?])(?=\s*[{[\d"tfn])/g, '$1 ')
        return JSON.parse(string)
    } catch (e) {
        console.error(string, id, e)
        return ''
    }
}

export const generateRandomString = (length) => {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return result
}

export const addDays = (days, date) => {
    const today = date
    return new Date(today?.getTime() + days * 24 * 60 * 60 * 1000)
}

export const splitArrayIntoChunks = (array, chunkSize = 10) => {
    const chunks = []
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
}

/**
 * Calculates the index of an item based on the page number, item position, and offset.
 * Ensures a progressive index across chunks within the same page.
 * When `isPlpPlus` is true and `page === 1`, the first page contains 14 products.
 *
 * @param {number} page - The current page number.
 * @param {number} position - The position of the item within the chunk.
 * @param {number} offset - The cumulative size of all previous chunks.
 * @param {boolean} isPlpPlus - Whether the page is PLP Plus.
 * @returns {number} The calculated index.
 */
export const calculateIndex = (page, position, offset, isPlpPlus) => {
    const productsPerPage = isPlpPlus && page === 1 ? 14 : 16
    return position + 1 + (page - 1) * productsPerPage + offset
}

/**
 * Retrieves the appropriate image for a product tile.
 *
 * @param {Object} product - The product object containing image and variant details.
 * @param {string|null} customImage - Optional custom image URL to override the default image.
 * @returns {string|null} The URL of the image to display for the product tile, or null if no image is found.
 */
export const getImageForProductTile = (product, customImage = null) => {
    let image = customImage ? customImage : product?.c_image?.url

    if (!image) {
        const productVariant = product?.c_variants?.find(
            (variant) => variant?.productId === product?.representedProduct?.id,
        )

        const variantImage = findImageGroupBy(product?.imageGroups, {
            viewType: 'small',
            selectedVariationAttributes:
                productVariant?.variationValues ?? product?.variationValues,
        })

        image = variantImage?.images?.[0]?.link
    }

    return image || null
}

export const decodeHtmlEntities = (text) => {
    if (!text) return ''
    const entities = {
        '&rsquo;': '’',
        '&lsquo;': '‘',
        '&quot;': '"',
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
    }
    return text.replace(/&[a-z]+;/g, (match) => entities[match] || match)
}

export const buildHmcString = (BRAND, retrievedProducts, answers) => {
    if (!answers) return
    const skus =
        retrievedProducts
            ?.map((p) => p?.c_variants?.[0]?.productId || p?.variants?.[0]?.productId)
            .filter(Boolean)
            .slice(0, 3) || []

    const answerEntries = Object.entries(answers).map(([, value], index) => [
        `answer${index + 1}`,
        value,
    ])

    return {
        hmc: {
            [BRAND]: {
                date: new Date().toISOString().slice(0, 10),
                skus,
                ...Object.fromEntries(answerEntries),
            },
        },
    }
}

export const buildCampaignTags = (
    psp,
    BRAND,
    BRAND_SHORT_LABEL,
    campaignTag,
    retrievedProducts,
    locale,
) => {
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '')
    const brandLocale = `${BRAND}${locale.toUpperCase()}`
    const skus =
        retrievedProducts
            ?.map((p) => p?.c_variants?.[0]?.productId || p?.variants?.[0]?.productId)
            .filter(Boolean)
            .slice(0, 3) || []

    if (psp) {
        return [`${today} ${campaignTag} ${brandLocale}`]
    } else {
        const skuString = skus.join('|')
        return [`${today} HMC${BRAND_SHORT_LABEL.toUpperCase()}-${skuString} ${brandLocale}`]
    }
}

export const hasOnlyNotAllowedMaterialGroups = (showGiftNote, basket) => {
    const notAllowedMaterialGroups = showGiftNote?.notAllowedMaterialGroups

    const onlyNotAllowedGroups =
        basket?.productItems?.every((item) =>
            notAllowedMaterialGroups?.includes(item?.c_MaterialGroup),
        ) ?? false

    return onlyNotAllowedGroups
}

export * from '@salesforce/retail-react-app/app/utils/utils.js'

/**
 * Loqate's Retrieve drops the street of PO-box-like addresses (e.g. DE Packstation returns
 * Line1/POBoxNumber "Packstation 124" only), while the Find suggestion the user clicked still
 * has it ("Packstation 124 Kurfürsten-Anlage 61 69115 Heidelberg").
 * Recovers the text between Line1 and the postcode; returns '' whenever the text doesn't match,
 * so the address falls back to Retrieve's lines.
 *
 * @param {Object} loqateAddress - Retrieve item, with the clicked Find text as SuggestionText
 * @returns {string} The street, or ''
 */
export const getLoqatePOBoxStreet = (loqateAddress) => {
    const {POBoxNumber, Line1, Line2, Line3, Line4, Line5, PostalCode, SuggestionText} =
        loqateAddress || {}

    if (!POBoxNumber || !Line1 || !PostalCode || !SuggestionText) return ''
    if (Line2 || Line3 || Line4 || Line5) return ''

    const collapse = (s) => s.replace(/\s+/g, ' ').trim()
    const text = collapse(SuggestionText)
    const prefix = collapse(Line1) + ' '

    if (!text.toLowerCase().startsWith(prefix.toLowerCase())) return ''

    // Padded so a postcode at the very end still matches; the last match wins
    const rest = ` ${text.slice(prefix.length)} `
    const postalCodeIndex = rest.lastIndexOf(` ${collapse(PostalCode)} `)
    if (postalCodeIndex === -1) return ''

    const street = rest.slice(0, postalCodeIndex).trim()

    // Must contain more than digits/punctuation to be a street
    return /[^\d\s.,/-]/.test(street) ? street : ''
}

export const arrangeLoqateLines = (loqateAddress) => {
    const twoLines = {Line1: '', Line2: ''}

    if (!loqateAddress) return twoLines

    const lines = [
        loqateAddress.Line1,
        getLoqatePOBoxStreet(loqateAddress),
        loqateAddress.Line2,
        loqateAddress.Line3,
        loqateAddress.Line4,
        loqateAddress.Line5,
    ]

    lines.forEach((al) => {
        if (!al) return

        if ((twoLines.Line1 + ' ' + al).length < 102 && twoLines.Line2 === '') {
            twoLines.Line1 += ' ' + al
            return
        }

        if ((twoLines.Line2 + ' ' + al).length < 102) {
            twoLines.Line2 += ' ' + al
        }
    })

    return {
        // substring, no trim, in order to avoid removing source spaces
        Line1: twoLines.Line1.substring(1),
        Line2: twoLines.Line2.substring(1),
    }
}

export const isUntypedTrue = (value) => value === true || value === 'true'

export const isWarrantyBasket = (basket) =>
    basket?.productItems?.some(
        (item) => item?.c_isWarranty === true || item?.c_isWarranty === 'true',
    ) ?? false

export const stripEmojis = (value) =>
    typeof value !== 'string'
        ? value
        : value.replace(
              new RegExp(
                  '\\p{Extended_Pictographic}|\\p{Emoji_Presentation}|\\p{Cf}|(?!\\d|#|\\*)\\p{Emoji_Component}',
                  'gu',
              ),
              '',
          )
